import { motion, MotionStyle } from 'framer-motion'
import React, { forwardRef } from 'react'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { media } from 'utils/media'
import { StarRating as StarRatingOriginal } from 'lib/StarRating'
import { TabBar } from 'lib/TabBar'
import { Text } from 'lib/Text'
import { useBrandStories } from '../queries/brandStories'
import { useCurrentBrand } from './context/CurrentBrandProvider'

const Container = styled.nav`
  border-bottom: 1px solid var(--neutral-20);
  margin-top: 44px;
  margin-bottom: 32px;

  ${media.phoneDown} {
    background: white;
    padding-top: 8px;
    position: sticky;
    margin-top: -48px;
    top: 0;
    z-index: 1;
  }
`

const HorizontalScroll = styled.div`
  overflow-x: auto;
  ::-webkit-scrollbar {
    display: none;
  }
  -webkit-overflow-scrolling: touch;

  ${media.tabletDown} {
    padding: 0 var(--grid-gap-small);
  }
`

const Nav = styled.nav`
  display: inline-block;
`

const MobileHeader = styled(motion.button)`
  align-items: center;
  background: transparent;
  border: 0;
  display: none;
  flex-direction: column;
  justify-content: center;
  max-height: 0;
  padding: 4px 8px;
  position: relative;
  width: 100%;

  ${media.phoneDown} {
    display: flex;
    max-height: 200px;
  }
`

const MobileBrandTitle = styled(Text)`
  flex-basis: 100%;
  line-height: 100%;
  margin-top: 2px;
`

const StarRating = styled(StarRatingOriginal)`
  flex-basis: 100%;
`

interface Props {
  style: MotionStyle
  onTabChange: () => void
}

export const StickyTabNavigation = forwardRef<HTMLDivElement, Props>(({ onTabChange, style }, ref) => {
  const { brand } = useCurrentBrand()
  const { data: storiesData } = useBrandStories(brand.id)
  const roundedRating = Math.round((brand?.reviewSummary?.averageRating ?? 0) * 10) / 10
  const isTablet = useMedia('(max-width: 922px)', false)

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTabClick = (tabName: string) => () => {
    window.analytics.track('Brand Tab Clicked', {
      brandId: brand.id,
      tabName
    })

    onTabChange()
  }

  return (
    <Container ref={ref}>
      <MobileHeader style={style} onClick={scrollToTop}>
        <MobileBrandTitle weight="semi" size="pxs">
          {brand.title}
        </MobileBrandTitle>
        <StarRating showCount={!!roundedRating} stars={roundedRating || 0} />
      </MobileHeader>
      <HorizontalScroll>
        <Nav>
          <TabBar
            ariaLabel="Content navigation"
            tabs={[
              {
                id: 'reviews',
                title: 'Reviews',
                href: `/brands/${brand.slug}/reviews`,
                match: new RegExp('^/brands/[^/]+(/reviews)?'),
                onClick: handleTabClick('reviews')
              },
              {
                id: 'info',
                title: 'Brand Info',
                href: `/brands/[slug]/info`,
                as: `/brands/${brand.slug}/info`,
                if: isTablet,
                onClick: handleTabClick('info')
              },
              {
                id: 'discounts',
                title: 'Discounts',
                href: `/brands/[slug]/discounts`,
                as: `/brands/${brand.slug}/discounts`,
                if: brand.hasDiscountCodes,
                onClick: handleTabClick('discounts')
              },
              {
                id: 'featured',
                title: 'Featured',
                href: `/brands/[slug]/featured`,
                as: `/brands/${brand.slug}/featured`,
                if: !storiesData?.brand || storiesData.brand.stories.length > 0,
                onClick: handleTabClick('featured')
              },
              {
                id: 'similar',
                title: 'Similar brands',
                href: `/brands/[slug]/similar`,
                as: `/brands/${brand.slug}/similar`,
                onClick: handleTabClick('similar')
              },
              {
                id: 'external-links',
                title: 'External links',
                href: `/brands/[slug]/external-links`,
                as: `/brands/${brand.slug}/external-links`,
                if: !!brand.externalLinks && brand.externalLinks.length > 0,
                onClick: handleTabClick('external-links')
              }
            ]}
            variant="underline"
          />
        </Nav>
      </HorizontalScroll>
    </Container>
  )
})

StickyTabNavigation.displayName = 'TabNavigation'
